import { formatLakh, ragLabel, ragPillClass } from "@/utils/blockDetailDerived";

export { formatLakh, ragLabel, ragPillClass };

const fmt = (v) => (typeof v === "number" ? v.toLocaleString("en-IN") : v ?? 0);

const titleCase = (name) => {
  if (!name) return "";
  const s = String(name);
  return s.charAt(0) + s.slice(1).toLowerCase();
};

export const districtHealthCopy = (district, blocks) => {
  const shi = district?.shi_score ?? 0;
  const sorted = [...blocks].sort((a, b) => (a.shi_score ?? 0) - (b.shi_score ?? 0));
  const bottom = sorted.slice(0, 3).map((b) => titleCase(b.block_name)).join(", ");

  if (shi >= 90) {
    return {
      title: "Excellent District Performance",
      body: `${titleCase(district?.district_name)} performs strongly across block-level OSH indicators. The key risk is not the overall score, but the lower tail: ${bottom || "selected blocks"} need focused review to prevent deterioration.`,
      action: "Maintain excellent status while launching a 30-day support sprint for the lowest three OSH blocks.",
    };
  }
  if (shi >= 75) {
    return {
      title: "Strong District Performance with Tail Risk",
      body: "District indicators are healthy overall, but the lowest-scoring blocks need weekly monitoring and targeted support.",
      action: "Run a focused review on bottom-quartile blocks and close data gaps within 30 days.",
    };
  }
  return {
    title: "District Performance Needs Intervention",
    body: "Multiple blocks are below the district target band. Prioritise identity completion and school-level data quality.",
    action: "Launch an immediate district-wide correction sprint for OSH and APAAR gaps.",
  };
};

export const buildDistrictInsightCards = (district, blocks) => {
  const sorted = [...blocks].sort((a, b) => (b.shi_score ?? 0) - (a.shi_score ?? 0));
  const bottom = [...blocks].sort((a, b) => (a.shi_score ?? 0) - (b.shi_score ?? 0));
  const atRisk = blocks.filter((b) => b.rag_status !== "green");
  const lowApaar = [...blocks].sort((a, b) => (a.apaar_percentage ?? 0) - (b.apaar_percentage ?? 0)).slice(0, 3);
  const scores = blocks.map((b) => b.shi_score ?? 0);
  const min = Math.min(...scores, 0);
  const max = Math.max(...scores, 0);

  return [
    {
      label: "Insight",
      title: "Overall Performance Is Stable",
      body: `Most blocks are within a ${min.toFixed(0)}–${max.toFixed(0)} OSH band, indicating consistent service delivery across the district.`,
    },
    {
      label: "Signal",
      title: "Risk Is Concentrated in the Lower Tail",
      body:
        atRisk.length > 0
          ? `${titleCase(atRisk[0]?.block_name)} is the primary at-risk block, while ${titleCase(bottom[1]?.block_name)} and ${titleCase(bottom[2]?.block_name)} should be monitored weekly.`
          : `${titleCase(bottom[0]?.block_name)} has the lowest OSH score; monitor nearby threshold blocks weekly.`,
    },
    {
      label: "Root Cause",
      title: "Identity Completion Gap",
      body: `Blocks with lower APAAR coverage, such as ${lowApaar.map((b) => titleCase(b.block_name)).join(", ")}, require targeted identity completion camps.`,
    },
    {
      label: "Recommendation",
      title: "Run a 30-Day Closure Sprint",
      body: "Prioritise block officers, school heads and data-entry teams for bottom-quartile APAAR and OSH correction.",
    },
  ];
};

export const buildWatchlist = (blocks) => {
  const sorted = [...blocks].sort((a, b) => (a.shi_score ?? 0) - (b.shi_score ?? 0));
  const items = sorted.slice(0, 3).map((block, idx) => {
    const isLowest = idx === 0;
    const isLarge = (block.total_students ?? 0) > 200000;
    return {
      block,
      score: block.shi_score ?? 0,
      variant: isLowest ? "orange" : idx === 1 ? "" : "green",
      title: titleCase(block.block_name),
      body: isLowest
        ? "Lowest OSH score and primary at-risk block. Review data completeness and school-level exceptions."
        : isLarge
          ? "Large student base with near-threshold OSH. Monitor weekly until score improves."
          : "Lower score relative to district band. Conduct focused review rather than broad intervention.",
    };
  });
  const scores = blocks.map((b) => b.shi_score ?? 0);
  const highest = Math.max(...scores, 0);
  const lowest = Math.min(...scores, 0);
  return { items, highest, lowest, spread: Math.round((highest - lowest) * 10) / 10 };
};

export const buildDomainCards = (district, blocks) => {
  const byStudents = [...blocks].sort((a, b) => (b.total_students ?? 0) - (a.total_students ?? 0));
  const lowApaar = [...blocks].sort((a, b) => (a.apaar_percentage ?? 0) - (b.apaar_percentage ?? 0)).slice(0, 2);
  return [
    {
      title: "School Coverage",
      score: fmt(district?.total_schools ?? 0),
      unit: "Schools",
      progress: 100,
      progressColor: "",
      metaLeft: `${blocks.length} blocks`,
      metaRight: "100% mapped",
      icon: "school",
      dot: "",
    },
    {
      title: "Student Base",
      score: formatLakh(district?.total_students ?? 0),
      unit: "Students",
      progress: 100,
      progressColor: "accent",
      metaLeft: `${titleCase(byStudents[0]?.block_name)} largest`,
      metaRight: `${titleCase(byStudents[byStudents.length - 1]?.block_name)} smallest`,
      icon: "users",
      dot: "",
    },
    {
      title: "Aadhaar Readiness",
      score: (district?.aadhaar_percentage ?? 0).toFixed(1),
      unit: "%",
      progress: district?.aadhaar_percentage ?? 0,
      progressColor: "purple",
      metaLeft: "Strong baseline",
      metaRight: "Low variance",
      icon: "shield",
      dot: "",
    },
    {
      title: "APAAR Completion",
      score: (district?.apaar_percentage ?? 0).toFixed(1),
      unit: "% avg",
      progress: district?.apaar_percentage ?? 0,
      progressColor: "orange",
      metaLeft: `Watch ${titleCase(lowApaar[0]?.block_name)}`,
      metaRight: titleCase(lowApaar[1]?.block_name),
      icon: "clipboard",
      dot: "amber",
    },
  ];
};

export const buildActionCards = (blocks) => {
  const sorted = [...blocks].sort((a, b) => (a.shi_score ?? 0) - (b.shi_score ?? 0));
  const lowest = sorted[0];
  const lowApaar = [...blocks].sort((a, b) => (a.apaar_percentage ?? 0) - (b.apaar_percentage ?? 0)).slice(0, 4);
  return [
    {
      variant: "orange",
      title: `Stabilise ${titleCase(lowest?.block_name)}`,
      body: "Review school-level exceptions, missing records and data-quality issues that are pulling OSH below the district band.",
      due: "Due: 7 days",
    },
    {
      variant: "purple",
      title: "Close APAAR Gaps",
      body: `Target ${lowApaar.map((b) => titleCase(b.block_name)).join(", ")} with data-entry follow-up and school-wise student identity validation.`,
      due: "Due: 30 days",
    },
    {
      variant: "green",
      title: "Protect Excellent Status",
      body: "Continue weekly monitoring of bottom-quartile blocks and publish exception closures before the next district review.",
      due: "Due: Weekly",
    },
  ];
};

export const blockStatusTag = (rag) => {
  if (rag === "green") return { label: "Excellent", className: "" };
  if (rag === "amber") return { label: "At Risk", className: "warning" };
  return { label: "Critical", className: "warning" };
};

export const filterAndSortBlocks = (blocks, { statusFilter, sortKey }) => {
  let rows = [...blocks];
  if (statusFilter === "excellent") rows = rows.filter((b) => b.rag_status === "green");
  if (statusFilter === "at-risk") rows = rows.filter((b) => b.rag_status !== "green");

  if (sortKey === "students") rows.sort((a, b) => (b.total_students ?? 0) - (a.total_students ?? 0));
  else if (sortKey === "aadhaar") rows.sort((a, b) => (b.aadhaar_percentage ?? 0) - (a.aadhaar_percentage ?? 0));
  else if (sortKey === "apaar") rows.sort((a, b) => (b.apaar_percentage ?? 0) - (a.apaar_percentage ?? 0));
  else rows.sort((a, b) => (b.shi_score ?? 0) - (a.shi_score ?? 0));

  return rows;
};

export const exportBlocksCsv = (rows) => {
  const headers = ["Block Name", "Block Code", "Schools", "Students", "Aadhaar %", "APAAR %", "OSH Score", "Status"];
  const lines = rows.map((b) => {
    const status = blockStatusTag(b.rag_status).label;
    return [
      `"${String(b.block_name || "").replace(/"/g, '""')}"`,
      b.block_code,
      b.total_schools ?? 0,
      b.total_students ?? 0,
      b.aadhaar_percentage ?? 0,
      b.apaar_percentage ?? 0,
      b.shi_score ?? 0,
      status,
    ].join(",");
  });
  const blob = new Blob([[headers.join(","), ...lines].join("\n")], { type: "text/csv;charset=utf-8;" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = "district_blocks.csv";
  a.click();
  URL.revokeObjectURL(a.href);
};
